<template>
  <div class="space-y-6">
    <!-- Header -->
    <div>
      <h2 class="text-xl font-semibold mb-2">Vunna affärer per vecka</h2>
      <p class="text-sm text-gray-600">Översikt över avslutade och vunna ordrar</p>
    </div>

    <!-- Week + Country selectors -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="bg-white rounded-lg shadow p-4 border-l-4" style="border-left-color: #3273DC">
        <div class="flex items-center gap-2 mb-3">
          <BaseIcon icon="calendar-alt" style="color: #3273DC" />
          <h3 class="text-base font-semibold">Veckoöversikt</h3>
        </div>
        <p class="text-sm text-gray-500 mb-3">Välj vecka för att se detaljerad statistik</p>
        <select
          :value="selectedWeek"
          @change="$emit('select-week', $event.target.value)"
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option v-if="weeks.length === 0" value="" disabled>Inga veckor tillgängliga</option>
          <option v-for="week in weeks" :key="week.key" :value="week.key">{{ week.label }}</option>
        </select>
      </div>

      <div class="bg-white rounded-lg shadow p-4 border-l-4" style="border-left-color: #3273DC">
        <div class="flex items-center gap-2 mb-3">
          <BaseIcon icon="map-marker-alt" style="color: #3273DC" />
          <h3 class="text-base font-semibold">Välj land</h3>
        </div>
        <p class="text-sm text-gray-500 mb-3">Filtrera affärer per land/region</p>
        <div class="space-y-2">
          <button
            v-for="c in countryOptions"
            :key="c.key"
            class="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm border-2 transition-colors"
            :class="selectedCountry === c.key ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'"
            @click="selectedCountry = c.key"
          >
            <span class="text-xl">{{ c.flag }}</span>
            <span>{{ c.label }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 5 Stat Cards -->
    <div v-if="selectedWeekInfo" class="grid grid-cols-1 md:grid-cols-5 gap-4">
      <div class="bg-white rounded-lg shadow p-6 border-2" style="border-color: #F1A239">
        <div class="flex items-center justify-between mb-2">
          <p class="text-sm text-gray-600">Ny MRR</p>
          <BaseIcon icon="dollar-sign" style="color: #F1A239" />
        </div>
        <p class="text-3xl font-semibold" style="color: #F1A239">{{ formatCurrency(weekData.value) }}</p>
        <div v-if="prevWeekData.value > 0" class="flex items-center gap-1 mt-2">
          <BaseIcon :icon="valueChange > 0 ? 'arrow-up' : valueChange < 0 ? 'arrow-down' : 'minus'" class="text-xs" :class="changeColor(valueChange)" />
          <span class="text-xs font-semibold" :class="changeColor(valueChange)">
            {{ valueChange > 0 ? '+' : '' }}{{ valueChange.toFixed(1) }}%
          </span>
          <span class="text-xs text-gray-500">vs förra v.</span>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-6 border-2" style="border-color: #3273DC">
        <div class="flex items-center justify-between mb-2">
          <p class="text-sm text-gray-600">Antal affärer</p>
          <BaseIcon icon="trophy" style="color: #3273DC" />
        </div>
        <p class="text-3xl font-semibold" style="color: #3273DC">{{ formatNumber(weekData.count) }}</p>
        <div v-if="prevWeekData.count > 0" class="flex items-center gap-1 mt-2">
          <BaseIcon :icon="countChange > 0 ? 'arrow-up' : countChange < 0 ? 'arrow-down' : 'minus'" class="text-xs" :class="changeColor(countChange)" />
          <span class="text-xs font-semibold" :class="changeColor(countChange)">
            {{ countChange > 0 ? '+' : '' }}{{ countChange.toFixed(1) }}%
          </span>
          <span class="text-xs text-gray-500">vs förra v.</span>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-6 border-2 border-gray-300">
        <div class="flex items-center justify-between mb-2">
          <p class="text-sm text-gray-600">Genomsnittsvärde</p>
          <BaseIcon icon="dollar-sign" class="text-gray-400" />
        </div>
        <p class="text-3xl font-semibold text-gray-900">{{ formatCurrency(weekData.avgValue) }}</p>
        <p class="text-xs text-gray-500 mt-1">per affär</p>
      </div>

      <div class="bg-white rounded-lg shadow p-6 border-2 border-gray-300">
        <div class="flex items-center justify-between mb-2">
          <p class="text-sm text-gray-600">Förra veckan</p>
          <BaseIcon icon="calendar-alt" class="text-gray-400" />
        </div>
        <p class="text-3xl font-semibold text-gray-900">{{ formatCurrency(prevWeekData.value) }}</p>
        <p v-if="prevWeekData.count > 0" class="text-xs text-gray-500 mt-1">
          {{ prevWeekData.count }} {{ prevWeekData.count === 1 ? 'affär' : 'affärer' }}
        </p>
      </div>

      <div class="bg-white rounded-lg shadow p-6 border-2" style="border-color: #3273DC">
        <div class="flex items-center justify-between mb-2">
          <p class="text-sm text-gray-600">Genomsnittlig Time to Close</p>
          <BaseIcon icon="clock" style="color: #3273DC" />
        </div>
        <p class="text-3xl font-semibold" style="color: #3273DC">
          {{ weekData.avgTimeToClose > 0 ? Math.round(weekData.avgTimeToClose) : '-' }}
          <span v-if="weekData.avgTimeToClose > 0" class="text-base font-normal text-gray-500">dagar</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function getISOWeekYear(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
}

function getMondayOfISOWeek(year, week) {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dow = simple.getUTCDay();
  const ISOweekStart = new Date(simple);
  if (dow <= 4) ISOweekStart.setUTCDate(simple.getUTCDate() - simple.getUTCDay() + 1);
  else ISOweekStart.setUTCDate(simple.getUTCDate() + 8 - simple.getUTCDay());
  return new Date(ISOweekStart.getUTCFullYear(), ISOweekStart.getUTCMonth(), ISOweekStart.getUTCDate());
}

function buildWeekEntry(year, weekNum) {
  const monday = getMondayOfISOWeek(year, weekNum);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const endHigh = new Date(sunday);
  endHigh.setHours(23, 59, 59, 999);
  return {
    key: `${year}-W${String(weekNum).padStart(2, "0")}`,
    label: `Vecka ${weekNum}, ${monday.toLocaleDateString("sv-SE", { month: "short", day: "numeric" })} - ${sunday.toLocaleDateString("sv-SE", { month: "short", day: "numeric", year: "numeric" })}`,
    startDate: monday,
    endDate: endHigh,
  };
}

function generateWeeks(deals) {
  const weekMap = new Map();
  (deals || []).forEach((deal) => {
    if (!deal.Avslutsdatum) return;
    try {
      const date = new Date(deal.Avslutsdatum);
      const wYear = getISOWeekYear(date);
      const wNum = getISOWeek(date);
      const key = `${wYear}-W${String(wNum).padStart(2, "0")}`;
      if (!weekMap.has(key)) weekMap.set(key, buildWeekEntry(wYear, wNum));
    } catch { /* skip */ }
  });

  // Always include the current week so we can default to it.
  const now = new Date();
  const cwYear = getISOWeekYear(now);
  const cwNum = getISOWeek(now);
  const currentKey = `${cwYear}-W${String(cwNum).padStart(2, "0")}`;
  if (!weekMap.has(currentKey)) weekMap.set(currentKey, buildWeekEntry(cwYear, cwNum));

  return Array.from(weekMap.values()).sort((a, b) => b.key.localeCompare(a.key));
}

const NORWAY_START_DATE = new Date("2026-01-01");
const NORWAY_SELLERS = ["Jiannis Zois", "Samuel Rosen", "Samuel Rosén"];
const CALLE = "Calle Baumgarten";

function filterDealsByCountry(deals, country) {
  return (deals || []).filter((deal) => {
    const seller = deal["Säljare"] || "";
    const dealDate = deal.Avslutsdatum ? new Date(deal.Avslutsdatum) : null;
    const isNorwaySeller =
      dealDate && dealDate >= NORWAY_START_DATE && NORWAY_SELLERS.includes(seller);

    if (country === "norway") return isNorwaySeller;
    if (country === "sweden-excl-calle") return !isNorwaySeller && seller !== CALLE;
    return !isNorwaySeller; // sweden-incl-calle
  });
}

function getCreatedDate(deal) {
  return deal["Skapad datum"] || deal["Skapad"] || deal["col_10"] || null;
}

export default {
  name: "PipelineWeeklyOverview",
  props: {
    wonDeals: { type: Array, default: () => [] },
    // Optional unfiltered set; when provided, the country selector operates on
    // it so all three options work regardless of any page-level Calle toggle.
    allDeals: { type: Array, default: null },
    selectedWeek: { type: String, default: "" },
  },
  data() {
    return {
      selectedCountry: "sweden-excl-calle",
      countryOptions: [
        { key: "sweden-excl-calle", label: "Sverige, exkl Calle", flag: "🇸🇪" },
        { key: "sweden-incl-calle", label: "Sverige, inkl Calle", flag: "🇸🇪" },
        { key: "norway", label: "Norge", flag: "🇳🇴" },
      ],
    };
  },
  computed: {
    sourceDeals() {
      return this.allDeals && this.allDeals.length ? this.allDeals : this.wonDeals;
    },
    countryFilteredDeals() {
      return filterDealsByCountry(this.sourceDeals, this.selectedCountry);
    },
    weeks() {
      return generateWeeks(this.countryFilteredDeals);
    },
    currentWeekKey() {
      const now = new Date();
      return `${getISOWeekYear(now)}-W${String(getISOWeek(now)).padStart(2, "0")}`;
    },
    selectedWeekInfo() {
      return this.weeks.find((w) => w.key === this.selectedWeek);
    },
    weekData() {
      const empty = { deals: [], value: 0, count: 0, avgValue: 0, avgTimeToClose: 0 };
      if (!this.selectedWeekInfo) return empty;

      const deals = this.countryFilteredDeals.filter((deal) => {
        if (!deal.Avslutsdatum) return false;
        try {
          const d = new Date(deal.Avslutsdatum);
          return d >= this.selectedWeekInfo.startDate && d <= this.selectedWeekInfo.endDate;
        } catch { return false; }
      });

      const value = deals.reduce((sum, d) => sum + (Number(d["Ordervärde"]) || 0), 0);
      const count = deals.length;

      let totalTtc = 0;
      let ttcCount = 0;
      deals.forEach((deal) => {
        const created = getCreatedDate(deal);
        if (!created || !deal.Avslutsdatum) return;
        try {
          const c = new Date(created);
          const closed = new Date(deal.Avslutsdatum);
          const days = Math.round((closed.getTime() - c.getTime()) / (1000 * 60 * 60 * 24));
          if (days >= 0) { totalTtc += days; ttcCount += 1; }
        } catch { /* skip */ }
      });

      return {
        deals,
        value,
        count,
        avgValue: count > 0 ? value / count : 0,
        avgTimeToClose: ttcCount > 0 ? totalTtc / ttcCount : 0,
      };
    },
    prevWeekData() {
      if (!this.selectedWeek) return { value: 0, count: 0 };
      const idx = this.weeks.findIndex((w) => w.key === this.selectedWeek);
      if (idx === -1 || idx >= this.weeks.length - 1) return { value: 0, count: 0 };
      const prev = this.weeks[idx + 1];
      const deals = this.countryFilteredDeals.filter((deal) => {
        if (!deal.Avslutsdatum) return false;
        try {
          const d = new Date(deal.Avslutsdatum);
          return d >= prev.startDate && d <= prev.endDate;
        } catch { return false; }
      });
      return {
        value: deals.reduce((sum, d) => sum + (Number(d["Ordervärde"]) || 0), 0),
        count: deals.length,
      };
    },
    valueChange() {
      if (this.prevWeekData.value > 0) {
        return ((this.weekData.value - this.prevWeekData.value) / this.prevWeekData.value) * 100;
      }
      return this.weekData.value > 0 ? 100 : 0;
    },
    countChange() {
      if (this.prevWeekData.count > 0) {
        return ((this.weekData.count - this.prevWeekData.count) / this.prevWeekData.count) * 100;
      }
      return this.weekData.count > 0 ? 100 : 0;
    },
  },
  watch: {
    weeks: {
      immediate: true,
      handler(val) {
        if (val.length === 0) return;
        const stillExists = val.some((w) => w.key === this.selectedWeek);
        if (!this.selectedWeek || !stillExists) {
          // Prefer the current week, fall back to most recent week with data.
          const current = val.find((w) => w.key === this.currentWeekKey);
          this.$emit("select-week", (current || val[0]).key);
        }
      },
    },
  },
  methods: {
    formatNumber(num) {
      return Number(num).toLocaleString("sv-SE", { maximumFractionDigits: 0 });
    },
    formatCurrency(num) {
      return Number(num).toLocaleString("sv-SE", { maximumFractionDigits: 0 }) + " kr";
    },
    changeColor(val) {
      if (val > 0) return "text-green-600";
      if (val < 0) return "text-red-600";
      return "text-gray-600";
    },
  },
};
</script>
